import React, { useState, useEffect, useRef, useCallback } from "react";
import * as API from "../api";

// ─── Design Tokens (dark theme) ─────────────────────────────
const D = {
  bg: "#0a0a0f",
  surface: "#111118",
  surfaceAlt: "#16161f",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(255,255,255,0.14)",
  text: "#f0f0fa",
  text2: "#8888aa",
  text3: "#55556a",
  purple: "#8b5cf6",
  pink: "#ec4899",
  blue: "#38bdf8",
  green: "#22d3a4",
  red: "#ef4444",
  orange: "#f59e0b",
  gradPurplePink: "linear-gradient(135deg, #8b5cf6, #ec4899)",
  gradBluePurple: "linear-gradient(135deg, #38bdf8, #8b5cf6)",
  glass: "rgba(255,255,255,0.04)",
  glassBorder: "rgba(255,255,255,0.08)",
};

// ─── SVG Icons ───────────────────────────────────────────────
const Icon = ({ d, size = 16, color = D.text2, strokeWidth = 1.7 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

// ─── Shared Card ─────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{
    background: D.surface,
    border: `1px solid ${D.border}`,
    borderRadius: 16,
    padding: 20,
    ...style,
  }}>
    {children}
  </div>
);

// ─── KPI Card ────────────────────────────────────────────────
const KpiCard = ({ label, value, gradient, icon }) => (
  <div style={{
    background: D.surface,
    border: `1px solid ${D.border}`,
    borderRadius: 16,
    padding: 20,
    flex: 1,
    minWidth: 160,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: 10, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon d={icon} size={15} color="#fff" strokeWidth={2} />
      </div>
      <span style={{ fontSize: 12, color: D.text2, fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color: D.text, letterSpacing: "-0.03em" }}>{value}</div>
  </div>
);

// ─── Inline Bar Chart ────────────────────────────────────────
const BarChart = ({ data }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.mrr), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 100 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ width: "100%", background: D.gradPurplePink, borderRadius: "4px 4px 0 0", height: `${Math.max(4, (d.mrr / max) * 80)}px`, opacity: 0.85 }} />
          <span style={{ fontSize: 9, color: D.text3, transform: "rotate(-45deg)", whiteSpace: "nowrap", transformOrigin: "center" }}>{d.month?.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Status Badge ────────────────────────────────────────────
const Badge = ({ status }) => {
  const colors = { active: D.green, trialing: D.blue, past_due: D.red, canceled: D.text3, ok: D.green, error: D.red };
  const c = colors[status] || D.text2;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: c, background: c + "22", borderRadius: 6, padding: "2px 8px" }}>
      {status}
    </span>
  );
};

// ─── Kill Switch Modal ────────────────────────────────────────
function KillSwitchModal({ title, description, onConfirm, onCancel, confirmLabel = "Confirm", danger = true }) {
  const [reason, setReason] = useState("");
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: 20, padding: 28, width: 420, maxWidth: "90vw" }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: D.text, marginBottom: 8 }}>{title}</h3>
        <p style={{ fontSize: 13, color: D.text2, marginBottom: 20, lineHeight: 1.6 }}>{description}</p>
        <textarea
          placeholder="Reason (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          style={{ width: "100%", background: D.bg, border: `1px solid ${D.border}`, borderRadius: 10, padding: "10px 14px", color: D.text, fontSize: 13, resize: "none", outline: "none", fontFamily: "inherit", marginBottom: 20 }}
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "8px 18px", borderRadius: 10, border: `1px solid ${D.border}`, background: "transparent", color: D.text2, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={() => onConfirm(reason)} style={{ padding: "8px 18px", borderRadius: 10, border: "none", background: danger ? D.red : D.purple, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Global Kill Switch Banner ────────────────────────────────
function KillSwitchBanner({ reason, onDeactivate }) {
  return (
    <div style={{ background: "#7f1d1d", borderBottom: "1px solid #ef444466", padding: "10px 24px", display: "flex", alignItems: "center", gap: 12 }}>
      <Icon d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" size={16} color="#fca5a5" strokeWidth={2} />
      <span style={{ fontSize: 13, color: "#fca5a5", fontWeight: 600 }}>GLOBAL KILL SWITCH ACTIVE{reason ? `: ${reason}` : ""}</span>
      <button onClick={onDeactivate} style={{ marginLeft: "auto", padding: "4px 14px", borderRadius: 8, border: "1px solid #ef444466", background: "transparent", color: "#fca5a5", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Deactivate</button>
    </div>
  );
}

// ─── Admin Chat ───────────────────────────────────────────────
function AdminChat() {
  const [messages, setMessages] = useState([{ role: "assistant", content: "Admin mode active. Ask me anything about the platform." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const { response } = await API.adminChat(msg, history);
      setMessages(m => [...m, { role: "assistant", content: response }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: `Error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ display: "flex", flexDirection: "column", height: 420 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 12 }}>Vella Admin Chat</div>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ maxWidth: "85%", alignSelf: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: D.text, background: m.role === "user" ? D.purple + "33" : D.glass, border: `1px solid ${D.border}`, borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", padding: "10px 14px" }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div style={{ alignSelf: "flex-start", fontSize: 12, color: D.text3, padding: "8px 14px" }}>Working on it...</div>}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Ask Vella..."
          style={{ flex: 1, background: D.bg, border: `1px solid ${D.border}`, borderRadius: 10, padding: "10px 14px", color: D.text, fontSize: 13, outline: "none", fontFamily: "inherit" }}
        />
        <button onClick={send} disabled={loading} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: D.gradPurplePink, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: loading ? 0.6 : 1 }}>Send</button>
      </div>
    </Card>
  );
}

// ─── Overview Page ────────────────────────────────────────────
function OverviewPage() {
  const [kpis, setKpis] = useState(null);
  const [activity, setActivity] = useState([]);
  const [topQ, setTopQ] = useState([]);
  const [chart, setChart] = useState([]);

  useEffect(() => {
    API.adminGetOverview().then(setKpis).catch(() => {});
    API.adminGetActivity(10).then(setActivity).catch(() => {});
    API.adminGetTopQuestions().then(setTopQ).catch(() => {});
    API.adminGetRevenueChart().then(setChart).catch(() => {});
  }, []);

  const fmt = v => v == null ? "—" : v.toLocaleString();
  const fmtMrr = v => v == null ? "—" : `$${v.toLocaleString()}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* KPI Row */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <KpiCard label="Total Users" value={fmt(kpis?.totalUsers)} gradient={D.gradPurplePink} icon="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        <KpiCard label="Active Subs" value={fmt(kpis?.activeSubscriptions)} gradient={D.gradBluePurple} icon="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        <KpiCard label="MRR" value={fmtMrr(kpis?.mrr)} gradient="linear-gradient(135deg, #22d3a4, #059669)" icon="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        <KpiCard label="AI Tasks Today" value={fmt(kpis?.aiTasksToday)} gradient="linear-gradient(135deg, #f59e0b, #ef4444)" icon="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        <KpiCard label="New Users Today" value={fmt(kpis?.newUsersToday)} gradient="linear-gradient(135deg, #38bdf8, #0ea5e9)" icon="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8M19 8l2 2-2 2M21 10h-4" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Revenue Chart */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 16 }}>MRR by Month</div>
          <BarChart data={chart} />
        </Card>

        {/* Top Questions */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 14 }}>Top AI Queries</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {topQ.slice(0, 6).map((q, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: D.text3, width: 18, textAlign: "right", flexShrink: 0 }}>{i + 1}</span>
                <span style={{ fontSize: 12, color: D.text2, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.query}</span>
                <span style={{ fontSize: 11, color: D.purple, fontWeight: 600, flexShrink: 0 }}>{q.count}x</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Live Activity */}
      <Card>
        <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 14 }}>Live Activity</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {activity.length === 0 && <span style={{ fontSize: 12, color: D.text3 }}>No recent activity.</span>}
          {activity.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", paddingBottom: 8, borderBottom: i < activity.length - 1 ? `1px solid ${D.border}` : "none" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: D.purple, marginTop: 6, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: D.text2 }}>{a.user?.email}</span>
                <span style={{ fontSize: 12, color: D.text3 }}> asked: </span>
                <span style={{ fontSize: 12, color: D.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", maxWidth: "60%", verticalAlign: "bottom" }}>{a.query}</span>
              </div>
              <span style={{ fontSize: 11, color: D.text3, flexShrink: 0 }}>{new Date(a.createdAt).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </Card>

      <AdminChat />
    </div>
  );
}

// ─── Users Page ───────────────────────────────────────────────
function UsersPage() {
  const [data, setData] = useState({ users: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [suspendModal, setSuspendModal] = useState(null); // { userId, email, currentlySuspended }

  const load = useCallback(async (p = page, s = search) => {
    setLoading(true);
    try {
      const res = await API.adminGetUsers(p, 25, s);
      setData(res);
    } catch { } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(page, search); }, [page]);

  const handleSearch = (e) => {
    if (e.key === "Enter") { setPage(1); load(1, search); }
  };

  const handleSuspend = async (reason) => {
    const { userId, currentlySuspended } = suspendModal;
    try {
      await API.adminSuspendUser(userId, !currentlySuspended, reason);
      setSuspendModal(null);
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {suspendModal && (
        <KillSwitchModal
          title={suspendModal.currentlySuspended ? `Unsuspend ${suspendModal.email}` : `Suspend ${suspendModal.email}`}
          description={suspendModal.currentlySuspended ? "This will restore access for this user." : "This will immediately block all AI/voice/creative requests from this user."}
          confirmLabel={suspendModal.currentlySuspended ? "Unsuspend" : "Suspend"}
          danger={!suspendModal.currentlySuspended}
          onConfirm={handleSuspend}
          onCancel={() => setSuspendModal(null)}
        />
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleSearch}
          placeholder="Search by name or email..."
          style={{ flex: 1, maxWidth: 320, background: D.surface, border: `1px solid ${D.border}`, borderRadius: 10, padding: "8px 14px", color: D.text, fontSize: 13, outline: "none", fontFamily: "inherit" }}
        />
        <span style={{ fontSize: 12, color: D.text3 }}>{data.total} users</span>
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${D.border}` }}>
              {["Name", "Email", "Plan", "Status", "AI Tasks", "Joined", ""].map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: D.text3, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", fontSize: 13, color: D.text3 }}>Loading...</td></tr>
            )}
            {!loading && data.users.map((u) => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${D.border}` }}>
                <td style={{ padding: "12px 16px", fontSize: 13, color: D.text }}>{u.name || "—"}</td>
                <td style={{ padding: "12px 16px", fontSize: 12, color: D.text2 }}>{u.email}</td>
                <td style={{ padding: "12px 16px" }}><Badge status={u.subscription?.plan || "none"} /></td>
                <td style={{ padding: "12px 16px" }}><Badge status={u.subscription?.status || "none"} /></td>
                <td style={{ padding: "12px 16px", fontSize: 12, color: D.text2 }}>{u.usage?.aiTasksUsed ?? "—"}</td>
                <td style={{ padding: "12px 16px", fontSize: 11, color: D.text3 }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: "12px 16px" }}>
                  {(() => {
                    const isSuspended = (u.suspended === true) || ((u.subscription?.status || "").toLowerCase() === "suspended");
                    return (
                      <button
                        onClick={() => setSuspendModal({ userId: u.id, email: u.email, currentlySuspended: isSuspended })}
                        style={{ padding: "4px 12px", borderRadius: 8, border: `1px solid ${D.border}`, background: "transparent", color: isSuspended ? D.green : D.red, cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                      >{isSuspended ? "Unsuspend" : "Suspend"}</button>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {data.pages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          {Array.from({ length: data.pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${page === p ? D.purple : D.border}`, background: page === p ? D.purple + "22" : "transparent", color: page === p ? D.purple : D.text2, cursor: "pointer", fontSize: 13, fontWeight: page === p ? 700 : 400 }}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Subscriptions Page ───────────────────────────────────────
function SubscriptionsPage() {
  const [data, setData] = useState(null);
  const [chart, setChart] = useState([]);

  useEffect(() => {
    API.adminGetSubscriptions().then(setData).catch(() => {});
    API.adminGetRevenueChart().then(setChart).catch(() => {});
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {(data?.breakdown || []).map(b => (
          <Card key={b.plan} style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: D.text, textTransform: "capitalize", marginBottom: 12 }}>{b.plan}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: D.text3 }}>Total</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: D.text }}>{b.count}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: D.text3 }}>Active</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: D.green }}>{b.active}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: D.text3 }}>Trial</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: D.blue }}>{b.trial}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: D.text3 }}>MRR</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: D.purple }}>${b.mrr.toLocaleString()}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 16 }}>Revenue Chart (MRR)</div>
        <BarChart data={chart} />
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 14 }}>Recent Subscriptions</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(data?.recent || []).map(s => (
            <div key={s.id} style={{ display: "flex", gap: 12, alignItems: "center", paddingBottom: 10, borderBottom: `1px solid ${D.border}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: D.text }}>{s.user?.email}</div>
                <div style={{ fontSize: 11, color: D.text3 }}>{new Date(s.createdAt).toLocaleDateString()}</div>
              </div>
              <Badge status={s.plan} />
              <Badge status={s.status} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Model Health Page ────────────────────────────────────────
function HealthPage() {
  const [health, setHealth] = useState(null);

  useEffect(() => { API.adminGetHealth().then(setHealth).catch(() => {}); }, []);

  const rows = health ? [
    { label: "Database", value: health.database },
    { label: "Redis", value: health.redis },
    { label: "Anthropic API", value: health.anthropic ? "configured" : "missing" },
    { label: "ElevenLabs API", value: health.elevenlabs ? "configured" : "missing" },
    { label: "Twilio", value: health.twilio ? "configured" : "missing" },
    { label: "Stripe", value: health.stripe ? "configured" : "missing" },
    { label: "Node Version", value: health.nodeVersion },
    { label: "Uptime", value: health.uptime ? `${Math.floor(health.uptime / 3600)}h ${Math.floor((health.uptime % 3600) / 60)}m` : "—" },
    { label: "Environment", value: health.env },
  ] : [];

  return (
    <Card>
      <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 16 }}>System Health</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows.map(r => (
          <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 12, borderBottom: `1px solid ${D.border}` }}>
            <span style={{ fontSize: 13, color: D.text2 }}>{r.label}</span>
            <Badge status={r.value} />
          </div>
        ))}
        {!health && <span style={{ fontSize: 13, color: D.text3 }}>Loading...</span>}
      </div>
    </Card>
  );
}

// ─── Alerts Page ─────────────────────────────────────────────
function AlertsPage() {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => { API.adminGetAlerts().then(setAlerts).catch(() => {}); }, []);

  const severityColor = { high: D.red, medium: D.orange, low: D.text3 };

  return (
    <Card>
      <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 16 }}>Alerts & Flags</div>
      {alerts.length === 0 && <span style={{ fontSize: 13, color: D.text3 }}>No alerts. All clear.</span>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {alerts.map((a, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderBottom: i < alerts.length - 1 ? `1px solid ${D.border}` : "none" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: severityColor[a.severity] || D.text3, marginTop: 4, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 12, color: D.text }}>{a.message}</span>
              <div style={{ fontSize: 11, color: severityColor[a.severity] || D.text3, marginTop: 3, fontWeight: 600, textTransform: "uppercase" }}>{a.severity}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Kill Switch Settings Page ────────────────────────────────
function KillSwitchPage({ killSwitch, onToggle, onRefresh }) {
  const [modal, setModal] = useState(null); // "activate" | "deactivate"

  const handleConfirm = async (reason) => {
    await onToggle(!killSwitch.active, reason);
    setModal(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 600 }}>
      {modal && (
        <KillSwitchModal
          title={modal === "activate" ? "Activate Global Kill Switch" : "Deactivate Kill Switch"}
          description={modal === "activate"
            ? "This will immediately block ALL AI, creative, and voice requests for ALL users. Only use this in an emergency."
            : "This will restore service for all users. Confirm to deactivate."}
          confirmLabel={modal === "activate" ? "Activate Kill Switch" : "Deactivate"}
          danger={modal === "activate"}
          onConfirm={handleConfirm}
          onCancel={() => setModal(null)}
        />
      )}

      <Card style={{ border: killSwitch?.active ? `1px solid ${D.red}44` : `1px solid ${D.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: killSwitch?.active ? D.red + "22" : D.surface, border: `1px solid ${killSwitch?.active ? D.red + "55" : D.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon d="M18.364 5.636a9 9 0 11-12.728 0M12 3v9" size={20} color={killSwitch?.active ? D.red : D.text2} strokeWidth={2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: D.text }}>Global Kill Switch</div>
            <div style={{ fontSize: 12, color: D.text2, marginTop: 3 }}>Blocks all AI, creative, and voice requests platform-wide.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: killSwitch?.active ? D.red : D.green }}>
              {killSwitch?.active ? "ACTIVE" : "OFF"}
            </span>
            <button
              onClick={() => setModal(killSwitch?.active ? "deactivate" : "activate")}
              style={{
                width: 52,
                height: 28,
                borderRadius: 14,
                border: "none",
                background: killSwitch?.active ? D.red : D.green + "55",
                cursor: "pointer",
                position: "relative",
                transition: "background 0.2s",
              }}
            >
              <div style={{
                position: "absolute",
                top: 3,
                left: killSwitch?.active ? 26 : 3,
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#fff",
                transition: "left 0.2s",
                boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
              }} />
            </button>
          </div>
        </div>
        {killSwitch?.active && killSwitch?.reason && (
          <div style={{ fontSize: 12, color: D.red, background: D.red + "11", border: `1px solid ${D.red}22`, borderRadius: 8, padding: "8px 12px" }}>
            Reason: {killSwitch.reason}
          </div>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 600, color: D.text, marginBottom: 12 }}>Per-User Suspension</div>
        <div style={{ fontSize: 12, color: D.text2, lineHeight: 1.6 }}>
          To suspend individual users, go to the Users page and click Suspend on any user row. Per-user suspension blocks only that user's requests without affecting the rest of the platform.
        </div>
      </Card>
    </div>
  );
}

// ─── Uptime indicator — pings /health every 30s ──────────────
// "checking" before the first probe so we don't show a stale green dot;
// "up" when the last probe succeeded, "down" on any error/timeout. The
// tooltip shows the latency + last-checked timestamp so an oncall admin
// can sanity-check whether the indicator is fresh.
function UptimeIndicator() {
  const [state, setState] = useState({ status: "checking", uptimeSec: null, latencyMs: null, checkedAt: null });

  useEffect(() => {
    let cancelled = false;
    async function probe() {
      const t0 = Date.now();
      try {
        const data = await API.getHealth();
        const latencyMs = Date.now() - t0;
        if (cancelled) return;
        setState({
          status: data?.status === "ok" ? "up" : "down",
          uptimeSec: typeof data?.uptime === "number" ? data.uptime : null,
          latencyMs,
          checkedAt: new Date(),
        });
      } catch {
        if (cancelled) return;
        setState((prev) => ({ ...prev, status: "down", latencyMs: Date.now() - t0, checkedAt: new Date() }));
      }
    }
    probe();
    const id = setInterval(probe, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const isUp = state.status === "up";
  const isChecking = state.status === "checking";
  const color = isChecking ? D.text2 : isUp ? D.green : D.red;
  const label = isChecking ? "Checking…" : isUp ? "Backend up" : "Backend down";
  const title = state.checkedAt
    ? `${label} · ${state.latencyMs ?? "?"}ms · checked ${state.checkedAt.toLocaleTimeString()}`
    : label;

  return (
    <div
      title={title}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 999, background: color + "18", border: `1px solid ${color}44`, color, fontSize: 11, fontWeight: 600, letterSpacing: 0.2 }}
    >
      <span
        style={{
          width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0,
          boxShadow: isUp ? `0 0 6px ${color}` : "none",
          animation: isChecking ? "vellaUptimePulse 1.2s ease-in-out infinite" : "none",
        }}
      />
      <span>{label}</span>
    </div>
  );
}

// ─── Main AdminDashboard Component ───────────────────────────
export default function AdminDashboard({ onExit }) {
  const [activePage, setActivePage] = useState("overview");
  const [killSwitch, setKillSwitch] = useState({ active: false, reason: null });

  const loadKillSwitch = useCallback(() => {
    API.adminGetKillSwitch().then(setKillSwitch).catch(() => {});
  }, []);

  useEffect(() => { loadKillSwitch(); }, [loadKillSwitch]);

  const handleToggleKillSwitch = async (active, reason) => {
    await API.adminSetKillSwitch(active, reason);
    loadKillSwitch();
  };

  const navItems = [
    { id: "overview", label: "Overview", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
    { id: "users", label: "Users", icon: "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75M12 7a4 4 0 110 8 4 4 0 010-8z" },
    { id: "subscriptions", label: "Subscriptions", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
    { id: "health", label: "Model Health", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
    { id: "alerts", label: "Alerts", icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" },
    { id: "kill-switch", label: "Kill Switch", icon: "M18.364 5.636a9 9 0 11-12.728 0M12 3v9" },
  ];

  const pages = {
    overview: <OverviewPage />,
    users: <UsersPage />,
    subscriptions: <SubscriptionsPage />,
    health: <HealthPage />,
    alerts: <AlertsPage />,
    "kill-switch": <KillSwitchPage killSwitch={killSwitch} onToggle={handleToggleKillSwitch} onRefresh={loadKillSwitch} />,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: D.bg, color: D.text, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif" }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(139,92,246,0.4); border-radius: 2px; }
        input, textarea { transition: border-color 0.15s; }
        input:focus, textarea:focus { border-color: rgba(139,92,246,0.6) !important; }
        @keyframes vellaUptimePulse { 0%,100% { opacity: 0.45; } 50% { opacity: 1; } }
      `}</style>

      {/* Kill switch banner */}
      {killSwitch.active && (
        <KillSwitchBanner reason={killSwitch.reason} onDeactivate={() => handleToggleKillSwitch(false, "")} />
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Internal Sidebar */}
        <div style={{ width: 220, background: D.surface, borderRight: `1px solid ${D.border}`, display: "flex", flexDirection: "column", padding: "20px 12px", flexShrink: 0 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28, padding: "0 8px" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: D.gradPurplePink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" size={14} color="#fff" strokeWidth={2.2} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: D.text }}>Admin</div>
              <div style={{ fontSize: 10, color: D.text3, marginTop: 1 }}>AMPLIFX Internal</div>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            {navItems.map(item => {
              const on = activePage === item.id;
              const isKill = item.id === "kill-switch";
              return (
                <button
                  key={item.id}
                  onClick={() => setActivePage(item.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 12px", borderRadius: 10, border: "none",
                    background: on ? (isKill ? D.red + "22" : D.purple + "22") : "transparent",
                    color: on ? (isKill ? D.red : D.purple) : D.text2,
                    cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: on ? 600 : 400,
                    transition: "background 0.15s, color 0.15s",
                  }}
                  onMouseEnter={e => { if (!on) { e.currentTarget.style.background = D.glass; e.currentTarget.style.color = D.text; } }}
                  onMouseLeave={e => { if (!on) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = D.text2; } }}
                >
                  <Icon d={item.icon} size={15} color={on ? (isKill ? D.red : D.purple) : D.text2} strokeWidth={on ? 2 : 1.7} />
                  {item.label}
                  {item.id === "kill-switch" && killSwitch.active && (
                    <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: D.red, flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Exit */}
          <button
            onClick={onExit}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 10, border: "none", background: "transparent", color: D.text3, cursor: "pointer", fontSize: 12, marginTop: 12, transition: "color 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.color = D.text; }}
            onMouseLeave={e => { e.currentTarget.style.color = D.text3; }}
          >
            <Icon d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" size={14} color="currentColor" strokeWidth={1.7} />
            Exit Admin
          </button>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Top Bar */}
          <div style={{ padding: "16px 28px", borderBottom: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <h1 style={{ fontSize: 17, fontWeight: 700, color: D.text, letterSpacing: "-0.02em" }}>
              {navItems.find(n => n.id === activePage)?.label || "Admin"}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <UptimeIndicator />
              {killSwitch.active && (
                <span style={{ fontSize: 11, fontWeight: 700, color: D.red, background: D.red + "22", border: `1px solid ${D.red}44`, borderRadius: 6, padding: "3px 10px", letterSpacing: "0.05em" }}>KILL SWITCH ON</span>
              )}
            </div>
          </div>

          {/* Page */}
          <div style={{ flex: 1, overflowY: "auto", padding: "28px" }}>
            {pages[activePage]}
          </div>
        </div>
      </div>
    </div>
  );
}
